import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

type StoryStatus = 'draft' | 'published';
type FilterTab = 'all' | StoryStatus | 'archived';

interface Story {
  id: string;
  title: string;
  excerpt: string;
  theme: string;
  themeAccent: string;
  status: StoryStatus;
  editedAt: string;
  wordCount: number;
}

const MOCK_STORIES: Story[] = [
  {
    id: '1',
    title: "The Fed's Quiet Pivot: What Markets Are Missing",
    excerpt: 'When the Federal Reserve met in November, the tone of the press conference was markedly different...',
    theme: 'Economist',
    themeAccent: '#E4002B',
    status: 'draft',
    editedAt: '2h ago',
    wordCount: 847,
  },
  {
    id: '2',
    title: 'Oil Supply Chains in 2025',
    excerpt: 'The disruption began not in the Middle East but in the Caspian basin, where three pipelines...',
    theme: 'Bloomberg',
    themeAccent: '#2D9CDB',
    status: 'published',
    editedAt: '3d ago',
    wordCount: 1204,
  },
  {
    id: '3',
    title: "Private Equity's New Playbook",
    excerpt: 'Across the industry, general partners are discovering that the buy-and-hold model requires...',
    theme: 'Financial Times',
    themeAccent: '#FF6B35',
    status: 'published',
    editedAt: '1w ago',
    wordCount: 2100,
  },
  {
    id: '4',
    title: 'Emerging Markets Report: Q2 2026',
    excerpt: 'Currency depreciation in frontier markets has accelerated beyond consensus forecasts...',
    theme: 'WSJ',
    themeAccent: '#003399',
    status: 'draft',
    editedAt: '2w ago',
    wordCount: 312,
  },
];

const FILTER_TABS: { id: FilterTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'published', label: 'Published' },
  { id: 'draft', label: 'Drafts' },
  { id: 'archived', label: 'Archived' },
];

export default function StoriesDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterTab>('all');
  const [search, setSearch] = useState('');

  const initials = user?.email?.slice(0, 2).toUpperCase() ?? 'JM';

  const filtered = MOCK_STORIES.filter((s) => {
    const matchesFilter = filter === 'all' || s.status === filter;
    const matchesSearch =
      !search ||
      s.title.toLowerCase().includes(search.toLowerCase()) ||
      s.excerpt.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const storyCount = filter === 'all' ? MOCK_STORIES.length : filtered.length;

  return (
    <div className="bg-ink-black text-on-surface min-h-screen flex flex-col font-body-md antialiased selection:bg-primary-container selection:text-ink-black">
      {/* Top nav */}
      <nav className="flex items-center justify-between px-6 py-3 bg-ink-black border-b border-border-subtle sticky top-0 z-40">
        <Link to="/" className="font-display-hero text-[20px] tracking-tighter text-primary uppercase">
          REPORTER
        </Link>
        <div className="flex items-center gap-4">
          <Link
            to="/editor"
            title="New story"
            aria-label="New story"
            className="flex items-center justify-center w-8 h-8 border border-primary text-primary hover:bg-primary hover:text-ink-black transition-colors"
          >
            <span className="material-symbols-outlined text-base leading-none">add</span>
          </Link>
          <button
            onClick={logout}
            title="Sign out"
            className="w-8 h-8 bg-primary-container text-ink-black font-label-caps text-[11px] uppercase flex items-center justify-center hover:bg-primary transition-colors"
          >
            {initials}
          </button>
        </div>
      </nav>

      <main className="flex-1 w-full max-w-2xl mx-auto px-4 md:px-6 py-8 flex flex-col gap-6">
        {/* Search */}
        <div className="relative">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-meta-gray text-[18px]">
            search
          </span>
          <input
            type="search"
            placeholder="Search stories..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-surface-container-low border border-border-subtle pl-10 pr-4 py-2 font-meta-code text-meta-code text-on-surface placeholder-meta-gray focus:outline-none focus:border-primary transition-colors"
            style={{ borderRadius: 0 }}
          />
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`flex-shrink-0 px-4 py-1.5 font-label-caps text-label-caps uppercase transition-colors border ${
                filter === tab.id
                  ? 'bg-primary text-ink-black border-primary'
                  : 'bg-transparent text-on-surface-variant border-border-subtle hover:border-primary hover:text-primary'
              }`}
              style={{ borderRadius: 0 }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Meta row */}
        <div className="flex justify-between items-center">
          <span className="font-meta-code text-[11px] text-meta-gray uppercase tracking-wider">
            {storyCount} {storyCount === 1 ? 'story' : 'stories'}
          </span>
          <button className="font-meta-code text-[11px] text-primary uppercase tracking-wider hover:opacity-80 transition-opacity">
            Last edited ↓
          </button>
        </div>

        {/* Story list */}
        <div className="flex flex-col">
          {filtered.length === 0 ? (
            <p className="font-meta-code text-meta-code text-meta-gray text-center py-16 uppercase tracking-widest">
              No stories found.
            </p>
          ) : (
            filtered.map((story) => (
              <button
                key={story.id}
                onClick={() => navigate('/editor')}
                className="text-left flex flex-col gap-1 px-0 py-4 border-b border-border-subtle hover:bg-surface-container-low transition-colors group"
              >
                {/* Title row */}
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-headline-md text-[16px] text-on-surface group-hover:text-primary transition-colors leading-tight">
                    {story.title}
                  </h3>
                  <span
                    className="flex-shrink-0 font-label-caps text-[9px] uppercase px-2 py-0.5 border mt-0.5"
                    style={{ color: story.themeAccent, borderColor: story.themeAccent + '55' }}
                  >
                    {story.theme}
                  </span>
                </div>
                {/* Excerpt */}
                <p className="font-meta-code text-[11px] text-meta-gray leading-relaxed line-clamp-1">
                  {story.excerpt}
                </p>
                {/* Meta */}
                <div className="flex items-center gap-3 mt-1">
                  <span
                    className={`font-label-caps text-[9px] uppercase px-1.5 py-0.5 border ${
                      story.status === 'published'
                        ? 'text-on-surface border-border-subtle'
                        : 'text-meta-gray border-border-subtle'
                    }`}
                  >
                    {story.status}
                  </span>
                  <span className="font-meta-code text-[10px] text-meta-gray">·</span>
                  <span className="font-meta-code text-[10px] text-meta-gray">{story.editedAt}</span>
                  <span className="font-meta-code text-[10px] text-meta-gray">·</span>
                  <span className="font-meta-code text-[10px] text-meta-gray">{story.wordCount.toLocaleString()} words</span>
                </div>
              </button>
            ))
          )}
        </div>
      </main>

      {/* Gold FAB — new story */}
      <Link
        to="/editor"
        aria-label="New story"
        className="fixed bottom-6 right-6 w-14 h-14 bg-primary text-ink-black flex items-center justify-center shadow-lg hover:bg-primary-container transition-colors z-50"
        style={{ borderRadius: '50%' }}
      >
        <span className="material-symbols-outlined text-2xl">add</span>
      </Link>

      {/* Bottom nav */}
      <nav className="bg-ink-black border-t border-border-subtle flex items-center justify-around py-3 sticky bottom-0 z-40">
        {[
          { icon: 'article', label: 'Stories', to: '/stories', active: true },
          { icon: 'explore', label: 'Explore', to: '/', active: false },
          { icon: 'settings', label: 'Settings', to: '#', active: false },
        ].map(({ icon, label, to, active }) => (
          <Link
            key={label}
            to={to}
            className={`flex flex-col items-center gap-1 transition-colors ${
              active ? 'text-primary' : 'text-meta-gray hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined text-[22px]">{icon}</span>
            <span className="font-label-caps text-[9px] uppercase tracking-wider">{label}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
}
